<template>
  <div class="min-h-screen bg-white dark:bg-gray-900">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <!-- Header -->
      <div class="text-center mb-16">
        <h1 class="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          VEDTEKTER FOR AKSJESPAREKLUBBEN ALLEGUTTA
        </h1>
        <p class="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Offisielle vedtekter for Aksjespareklubben Allegutta vedtatt 29. januar 2014, sist endret 22. desember 2021
        </p>
      </div>

      <!-- Vedtekter Content -->
      <div class="prose prose-lg dark:prose-invert max-w-none">
        <!-- § 1 NAVN OG REGISTRERING -->
        <section id="navn" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 1 NAVN OG REGISTRERING
          </h2>
          <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
            Klubbens navn er "Aksjespareklubben Allegutta".
            Det opprettes aksjekonto i en eller flere banker med tilhørende VPS-konto
          </p>
        </section>

        <!-- § 2 FORMÅL -->
        <section id="formaal" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 2 FORMÅL
          </h2>
          <div class="space-y-4">
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Klubbens formål er: å gi medlemmene innsikt og forståelse for
              aksjemarkedet og næringslivet for øvrig og å bidra til medlemmenes
              personlige sparing ved for deres felles regning å erverve og forvalte
              aksjer. Aksjespareklubben skal også være et sosialt nettverk.
              Med aksjer menes her aksjer, fond og børsnoterte fond som på anskaffelsestidspunktet
              er notert på Oslo Børs. - OBX, OSEBX og Euronext Growth. Det skal ikke handles i
              warrants eller derivater.
            </p>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Siden Aksjespareklubbens medlemmer kan være bundet av Kredittilsynets og
              Finansinspektionens til enhver tid gjeldende regler for egenhandel, gjelder de samme
              regler ved kjøp og salg. Dersom andre medlemmer er forhindret av interne regler som
              gjelder for ansatte i den bedriften medlemmet jobber, som for eksempel at de ikke har
              tillatelse til å investere i spesifikke selskap, så skal Aksjeklubben også overholde disse
              reglene.
            </p>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Aksjespareklubben skal basert på ovenstående ha en kjøp og behold (buy & hold)
              strategi. Aksjespareklubbens øvrige strategi vil i detalj fastsettes av medlemmene i
              fellesskap på medlemsmøter og spesifiseres i eget vedlegg. Vedlegg 1 -
              Investeringsstrategi. Det er ikke krav om 2/3 flertall for å gjøre endringer i vedlegg 1 -
              Investeringsstrategi.
            </p>
          </div>
        </section>

        <!-- § 3 MEDLEMSKAP -->
        <section id="medlemskap" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 3 MEDLEMSKAP
          </h2>
          <div class="space-y-4">
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Kun personer over 18 år kan opptas som medlemmer. Nye medlemmer kan
              opptas såfremt 2/3 av de eksisterende medlemmer samtykker. Nye medlemmer skal
              legitimeres med kopi av gyldig ID, attestert av godkjent myndighet.
              Hvert medlem eier minst én (1) og ikke flere enn seksten (16) andeler (fra 01.01.22)
              Kun medlemmer kan eie andeler.
            </p>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Oversikt over medlemmene med angivelse av andelsfordeling og tillitsverv
              følger vedtektene som vedlegg A.
            </p>
          </div>
        </section>

        <!-- § 4 KAPITAL -->
        <section id="kapital" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 4 KAPITAL
          </h2>
          <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
            Klubbens kapital er ved stiftelsen den 29. januar 2014 fordelt på 38 andeler à
            kr. 5.000. Hvert medlem skal innen 29. januar ha innbetalt sin(e) andel(er).
            Til kapitalen legges et kvartalsvis sparebeløp pr. andel som fastsettes
            årlig av årsmøtet med minst ¾-flertall. Sparebeløpet skal innbetales
            første gang innen 1. april 2014 og deretter innen den 1. i hvert
            kvartal. Pengene skal innbetales til den av leder angitte konto, p.t. bankkonto
            9710.32.19128 som disponeres av Bjørnar Næss, som betaler inn til Aksjespareklubbens
            depotkonto så snart alle andelseiere har betalt inn til bankkonto.
            Kapitalen kan økes ved utstedelse av andeler til nye eller eksisterende
            medlemmer og reduseres ved uttreden eller delvis uttreden. Jfr. <a href="#uttreden" class="text-primary-500 hover:text-primary-600">§ 11</a> og
            <a href="#delvisuttreden" class="text-primary-500 hover:text-primary-600">§ 12</a>.
          </p>
        </section>

        <!-- § 5 INNTEKTER - UTGIFTER -->
        <section id="inntekter" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 5 INNTEKTER - UTGIFTER
          </h2>
          <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
            Alle utbytter, gevinster og andre inntekter skal tillegges kapitalen.
            Klubbens driftsutgifter og eventuelle tap dekkes av kapitalmidlene.
          </p>
        </section>

        <!-- § 6 ORGANISASJON -->
        <section id="organisasjon" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 6 ORGANISASJON
          </h2>
          <div class="space-y-6">
            <!-- Årsmøte -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Årsmøte</h3>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                Årsmøte skal avholdes årlig og innen utgangen av oktober. Styret
                innkaller til årsmøtet. Innkalling med saksdokumenter skal sendes
                medlemmene senest to uker før årsmøtet.
              </p>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
                Følgende saker skal behandles på årsmøtet:
              </p>
              <ol class="list-decimal list-inside space-y-1 text-gray-600 dark:text-gray-300 ml-4">
                <li>Styrets beretning</li>
                <li>Fastsettelse av resultatregnskap og balanse</li>
                <li>Revisors rapport</li>
                <li>Fastsettelse av sparebeløp i h.h.t. <a href="#kapital" class="text-primary-500 hover:text-primary-600">§ 4</a>, 2. ledd</li>
                <li>Eventuelle vedtektsendringer</li>
                <li>Valg av leder, styre og revisor</li>
              </ol>
            </div>

            <!-- Styret -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Styret</h3>
              <div class="space-y-3">
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Styret består av leder, samt 2 - 3 medlemmer.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Styret velges for to år av gangen.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Styret forestår den daglige ledelse og er ansvarlig for føring av
                  klubbens regnskaper og protokoller. Styret plikter å ajourføre
                  vedtektenes vedlegg A (jfr. <a href="#medlemskap" class="text-primary-500 hover:text-primary-600">§ 3</a>), samt underrette bank- og
                  fondsmeglerforbindelser om endringer i vedtektene, inkludert vedlegg A.
                </p>
              </div>
            </div>

            <!-- Medlemsmøter -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. Medlemsmøter</h3>
              <div class="space-y-3">
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Styret kan innkalle til medlemsmøte med tre dagers varsel.
                  Møtene ledes av lederen, eller, i hans fravær, stedfortrederen.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Avgjørelser om kjøp og salg av aksjer fattes på medlemsmøtene eller av
                  andre retningslinjer gitt på sist avholdte medlemsmøte.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Det kan ikke disponeres over mer enn det til enhver tid er dekning for i
                  klubbens aktiva, jfr. <a href="#signaturansvar" class="text-primary-500 hover:text-primary-600">§ 7</a>.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Medlemsmøtet er beslutningsdyktig når ½-parten av medlemmene er
                  representert. Medlemmer kan gi andre medlemmer fullmakt til å representere disse på
                  medlemsmøtet, fullmakt gis ved å sende en mail til styrets leder, jfr. <a href="#votering" class="text-primary-500 hover:text-primary-600">§ 9</a>.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Møtet behandler spørsmål om opptak av nye medlemmer. Innkalling, med
                  opplysninger om søkeren og hvor mange andeler vedkommende ønsker å
                  tegne, skal i slike tilfeller sendes medlemmene senest to uker før
                  møtet.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- § 7 SIGNATURANSVAR -->
        <section id="signaturansvar" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 7 SIGNATURANSVAR
          </h2>
          <div class="space-y-4">
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Lederen forplikter klubben.
            </p>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Lederen er bundet av de instrukser hun/han mottar av medlemsmøtet og står
              personlig ansvarlig for tap hun/han måtte påføre klubben ved overtredelse av disse.
            </p>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Medlemmene hefter solidarisk for klubbens forpliktelser (jfr. Vedlegg A).
            </p>
          </div>
        </section>

        <!-- § 8 VEDTEKTSENDRINGER -->
        <section id="vedtektsendringer" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 8 VEDTEKTSENDRINGER
          </h2>
          <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
            Vedtektsendringer vedtas med årsmøtet med 2/3-dels flertall.
          </p>
        </section>

        <!-- § 9 VOTERING -->
        <section id="votering" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 9 VOTERING
          </h2>
          <div class="space-y-4">
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Hvert medlem har én stemme, uavhengig av antall andeler. Ved stemmelikhet har leder dobbeltstemme.
            </p>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Et medlem kan la seg representere av fullmektig som selv er medlem av klubben.
            </p>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              Alle stemmesaker avgjøres ved simpelt flertall blant de representerte
              medlemmene der ikke annet er bestemt.
            </p>
          </div>
        </section>

        <!-- § 10 PROTOKOLL -->
        <section id="protokoll" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 10 PROTOKOLL
          </h2>
          <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
            Det skal føres protokoll (referat) fra årsmøte, styremøter og medlemsmøter.
            Protokollen skal til enhver tid være tilgjengelig for klubbens medlemmer.
          </p>
        </section>

        <!-- § 11 UTTREDEN OG EKSKLUSJON - INNTREDEN -->
        <section id="uttreden" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 11 UTTREDEN OG EKSKLUSJON - INNTREDEN
          </h2>
          <div class="space-y-6">
            <!-- Uttreden -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Uttreden</h3>
              <div class="space-y-3">
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Uttreden skjer normalt pr. 31. desember og skal skriftlig meddeles
                  styret innen 30. september.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Den uttredendes andel(er) av aksjebeholdningen beregnes på grunnlag av
                  markedsverdien pr. uttredelsesdato. Ved hel eller delvis uttreden etter
                  mindre enn 12 måneder medlemskap trekkes 5% fra den uttredendes andel.
                  Ved uttreden etter 12 - 24 måneders medlemskap trekkes 2,5% fra.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Et medlem kan tre ut i løpet av året dersom vedkommendes andeler,
                  rettigheter og plikter etter disse vedtekter, i sin helhet overdras til
                  annet (andre) medlem(mer) (jfr. dog <a href="#medlemskap" class="text-primary-500 hover:text-primary-600">§ 3</a>, 3. ledd) eller til en
                  utenforstående som klubben godkjenner slik <a href="#medlemskap" class="text-primary-500 hover:text-primary-600">§ 3</a>, 1. ledd bestemmer.
                  Oppgjør i forbindelse med overdragelsen skjer mellom partene og er
                  klubben uvedkommende.
                </p>
              </div>
            </div>

            <!-- Konkurs eller død -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Konkurs eller død</h3>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                Kommer et medlem under konkursbehandling eller avgår ved døden, skal
                hans andel beregnes på grunnlag av siste omsetningskurs før konkurs-
                eller dødsdagen og utbetales boet. Boet kan, om de ønsker det,
                opprettholde medlemskapet frem til første årsskifte hvorpå uttreden
                skjer på ordinær måte.
              </p>
            </div>

            <!-- Eksklusjon -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. Eksklusjon</h3>
              <div class="space-y-3">
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Et medlem kan ekskluderes dersom 2/3 av samtlige medlemmer i
                  medlemsmøtet stemmer for dette. Eksklusjonsforslaget må bekjentgjøres
                  og begrunnes minst to uker før det skal behandles.
                  Eksklusjon må begrunnes i alvorlige forsømmelser eller gjentatte brudd
                  på forpliktelser etter disse vedtekter eller andre forhold som på
                  lignende måte skader klubbens interesser. Eksklusjonen er effektiv fra
                  den dag den besluttes.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Ekskludert medlems andel fastsettes som det laveste av følgende to beløp:
                </p>
                <ol type="a" class="list-[lower-alpha] list-inside space-y-1 text-gray-600 dark:text-gray-300 ml-4">
                  <li>medlemmets innbetalinger (startinnskudd og månedlige sparebeløp, ref. <a href="#kapital" class="text-primary-500 hover:text-primary-600">§ 4</a>) eller</li>
                  <li>90% av medlemmets andel vurdert slik denne paragraf bestemmer for
                    uttreden. Uttredelsesdato tilsvarende eksklusjonsdato. Ekskludert
                    medlems andel utbetales innen to uker.</li>
                </ol>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Begrunnes eksklusjonen i manglende innbetaling av det månedlige
                  sparebeløp eller i at klubben på annen måte har et økonomisk krav på
                  vedkommende, skal klubbens tilgodehavende fratrekkes andelen ved
                  utbetaling.
                </p>
              </div>
            </div>

            <!-- Inntreden -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">4. Inntreden</h3>
              <div class="space-y-3">
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Nytt medlem kan tre inn etter bestemmelsene i <a href="#medlemskap" class="text-primary-500 hover:text-primary-600">§ 3</a>, 1. ledd, ref <a href="#organisasjon" class="text-primary-500 hover:text-primary-600">§ 6.3</a>, 5. ledd.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Det nye medlem foretar innbetaling som tilsvarer andelens verdi i h.h.t.
                  klubbens regnskaper pr. 31. desember. Inntreden skjer pr. 1. januar.
                  Andelen(e)s verdi skal innbetales umiddelbart etter avholdt årsmøte,
                  senest innen utgangen av januar.
                </p>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Inntreden kan skje i løpet av året som følge av uttreden i h.h.t. denne
                  paragrafs punkt 1, siste avsnitt.
                </p>
              </div>
            </div>

            <!-- Kostnader -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">5. Kostnader</h3>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                Kostnader direkte knyttet til uttreden, eksklusjon og inntreden dekkes
                av det uttredende, ekskluderte eller inntredende medlem.
              </p>
            </div>
          </div>
        </section>

        <!-- § 12 DELVIS UTTREDEN OG INNTREDEN -->
        <section id="delvisuttreden" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 12 DELVIS UTTREDEN OG INNTREDEN
          </h2>
          <div class="space-y-6">
            <!-- Delvis uttreden -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Delvis uttreden</h3>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                Et medlem kan kreve seg utløst for en eller flere av sine andeler, jfr.
                dog <a href="#medlemskap" class="text-primary-500 hover:text-primary-600">§ 3</a>, 2. ledd. Ved delvis uttreden gjelder de samme frister og
                beregningsmåter som for uttreden, jfr. <a href="#uttreden" class="text-primary-500 hover:text-primary-600">§ 11</a>.
              </p>
            </div>

            <!-- Delvis inntreden -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Delvis inntreden</h3>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                Et medlem kan tre inn med nye andeler (jfr. dog <a href="#medlemskap" class="text-primary-500 hover:text-primary-600">§ 3</a>, 2. ledd) hver år 1.
                januar ved å innbetale andelen(e)s verdi i h.h.t. klubbens regnskaper
                pr. 31. desember det foregående år. Delvis inntreden krever ikke
                særskilt godkjennelse, men må skriftlig meddeles lederen innen 1.
                desember. Andelen(e)s verdi skal innbetales umiddelbart etter avholdt
                årsmøte, senest innen utgangen av april.
              </p>
            </div>

            <!-- Kostnader -->
            <div>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. Kostnader</h3>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                Kostnader direkte knyttet til delvis inn- eller uttreden dekkes av det
                delvis uttredende eller delvis inntredende medlem.
              </p>
            </div>
          </div>
        </section>

        <!-- § 13 OPPLØSNING -->
        <section id="opploesning" class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            § 13 OPPLØSNING
          </h2>
          <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
            Oppløsning av klubben skal skje dersom minst 2/3 av samtlige medlemmer
            stemmer for det på et årsmøte der dette er satt på dagsorden i
            innkallelsen.
          </p>
        </section>

        <!-- Footer information -->
        <section class="mt-16 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
            <div class="space-y-2">
              <p class="text-sm text-gray-500 dark:text-gray-400 text-center">
                Sist endret: 22. desember 2021 - Bjørnar Næss
              </p>
              <div class="text-sm text-gray-500 dark:text-gray-400 text-center space-y-1">
                <p>Vedlegg 1 - Investeringsstrategi</p>
                <p>Vedlegg 2 - Medlemsoversikt</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Page metadata
definePageMeta({
  title: 'Vedtekter for Aksjespareklubben Allegutta',
  description: 'Offisielle vedtekter for Aksjespareklubben Allegutta vedtatt 29. januar 2014, sist endret 22. desember 2021.',
})

// Use head for SEO
useHead({
  title: 'Vedtekter for Aksjespareklubben Allegutta - AlleGutta',
  meta: [
    {
      name: 'description',
      content: 'Offisielle vedtekter for Aksjespareklubben Allegutta vedtatt 29. januar 2014, sist endret 22. desember 2021. Komplett regelverk for medlemskap, organisasjon og drift av aksjespareklubben.'
    },
    {
      property: 'og:title',
      content: 'Vedtekter for Aksjespareklubben Allegutta - AlleGutta'
    },
    {
      property: 'og:description',
      content: 'Offisielle vedtekter for Aksjespareklubben Allegutta vedtatt 29. januar 2014, sist endret 22. desember 2021.'
    },
    {
      property: 'og:type',
      content: 'website'
    }
  ]
})
</script>
